import {
  defaultNavigationState,
  RefinePopupContextProps,
  setNavigationStateToLocalStore,
  StartOverDialogProps,
  useDiagnosticAgentNavigationState,
  UserManagementPopupMode,
  EditPanelPopupContextProps,
  PatientFirstSummaryDialogProps,
  FeedBackDialogProps,
} from "../../context/diagnosticAgent";
import { useEffect, useMemo } from "react";
import { useUserStorage } from "fe-web-components/userStorage/useUserStorage";

export function useDiagnosticNavigation() {
  const [
    diagnosticNavigationState,
    setDiagnosticNavigationState,
  ] = useDiagnosticAgentNavigationState();

  const userStorage = useUserStorage();

  const setters = useMemo(
    () => ({
      setInWizard: (inWizard: boolean) =>
        setDiagnosticNavigationState((prev) => ({ ...prev, inWizard })),
      setScrollLocation: (ddScrollLocation: any) =>
        setDiagnosticNavigationState((prev) => ({ ...prev, ddScrollLocation })),
      setExpandedDisease: (id: string, expanded: boolean) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          expandedDiseases: { ...prev.expandedDiseases, [id]: expanded },
        })),
      resetExpandedDiseases: () =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          expandedDiseases: {},
        })),
      setMenuOpen: (menuOpen: boolean) =>
        setDiagnosticNavigationState((prev) => ({ ...prev, menuOpen })),
      setRefinePopupProps: (refinePopupProps: RefinePopupContextProps) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          refinePopupProps: refinePopupProps.refineQualifier
            ? { ...refinePopupProps }
            : null,
        })),
      setEditPanelPopupProps: (
        editPanelPopupProps: EditPanelPopupContextProps,
      ) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          editPanelPopupProps: { ...editPanelPopupProps },
        })),
      setStartOverDialogProps: (startOverDialogProps: StartOverDialogProps) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          startOverDialogProps,
        })),

      setPatientFirstSummaryDialogProps: (
        patientFirstSummaryDialogProps: PatientFirstSummaryDialogProps,
      ) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          patientFirstSummaryDialogProps,
        })),

      setAddDiseaseOpen: (addDiseaseOpen: boolean) =>
        setDiagnosticNavigationState((prev) => ({ ...prev, addDiseaseOpen })),
      setCaseMismatchDialogOpen: (caseMismatchDialogOpen: boolean) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          caseMismatchDialogOpen,
        })),
      setImproveDiagnosisPopupOpen: (improveDiagnosisPopupOpen: boolean) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          improveDiagnosisPopupOpen,
        })),
      setLoginPopupState: (
        state: UserManagementPopupMode,
        forceLogin: boolean = false,
      ) =>
        setDiagnosticNavigationState((prev) => {
          let loginPopupMessage = prev.loginPopupMessage;
          // Clear the message when the popup is closed
          if (state === UserManagementPopupMode.Closed) loginPopupMessage = "";
          return {
            ...prev,
            loginPopupMessage,
            loginPopupState: state,
            forceLogin,
          };
        }),
      setLoginPopupMessage: (loginPopupMessage: string) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          loginPopupMessage,
        })),
      setNextRouteNavigationDisabled: (isIt: boolean) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          nextRouteNavigationDisabled: isIt,
        })),
      setShowPwaInstallWindow: (show: boolean) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          showPwaInstallWindow: show,
        })),
      setShowIosInstallPwaMessage: (show: boolean) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          showIosInstallPwaMessage: show,
        })),
      setToastText: (toastText: string) =>
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          toastText: toastText,
        })),
      setToastOpen: (toastOpen: boolean) => {
        if (toastOpen) {
          setDiagnosticNavigationState((prev) => ({
            ...prev,
            toastOpen: false,
          }));
          setTimeout(() => {
            setDiagnosticNavigationState((prev) => ({
              ...prev,
              toastOpen: true,
            }));
          }, 100);
        } else {
          setDiagnosticNavigationState((prev) => ({
            ...prev,
            toastOpen: false,
          }));
        }
      },
      setFeedbackDialogProps: (feedbackDialogProps: FeedBackDialogProps) => {
        setDiagnosticNavigationState((prev) => ({
          ...prev,
          feedbackDialogProps,
        }));
      },
      reset: () => setDiagnosticNavigationState(defaultNavigationState),
    }),
    [setDiagnosticNavigationState],
  );

  useEffect(
    () =>
      setNavigationStateToLocalStore(diagnosticNavigationState, userStorage),
    [diagnosticNavigationState, userStorage],
  );

  const diagnosticNavigation = useMemo(
    () => ({ ...diagnosticNavigationState, ...setters }),
    [diagnosticNavigationState, setters],
  );
  return diagnosticNavigation;
}
